import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { getReviewCards, reviewUserVocab, getUserVocabs } from "../services/UserVocabService";

/**
 * SRS intervals in days per level (0-5)
 * Level 0 → 1d | 1 → 2d | 2 → 4d | 3 → 7d | 4 → 15d | 5 → 30d
 */
export const SRS_INTERVALS = [1, 2, 4, 7, 15, 30];

/**
 * Flatten a UserVocab document (which has masterVocabId populated)
 * into the flat card shape expected by Flashcard / QuizCard:
 *   { _id, word, pinyin, meaning, example, hskLevel, topicId }
 * _id stays as the UserVocab _id so SRS PATCH uses it directly.
 */
function flattenCard(userVocab) {
  const m = userVocab?.masterVocabId ?? {};
  return {
    _id: userVocab._id,
    word: m.word ?? "",
    pinyin: m.pinyin ?? "",
    meaning: m.meaning ?? "",
    example: m.example ?? "",
    hskLevel: m.hskLevel ?? 1,
    topicId: m.topicId ?? null,
    srsLevel: userVocab.srsLevel ?? 0,
    nextReviewDate: userVocab.nextReviewDate,
  };
}

export default function useSRS(scope = "all", scopeId = "") {
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionDone, setSessionDone] = useState(false);
  const [reviewed, setReviewed] = useState(0);

  const filterByScope = useCallback((list) => {
    if (scope === "all") return list;
    if (scope === "topic") {
      return list.filter((c) => String(c.topicId?._id || c.topicId) === String(scopeId));
    }
    return list;
  }, [scope, scopeId]);

  const fetchCards = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getReviewCards();
      let due = (res?.data ?? []).map(flattenCard);

      if (scope === "folder") {
        const folderList = await getUserVocabs({ page: 1, limit: 1000, folderId: scopeId });
        const allowed = new Set((folderList?.data || []).map((v) => String(v._id)));
        due = due.filter((c) => allowed.has(String(c._id)));
      } else {
        due = filterByScope(due);
      }

      setCards(due);
      setCurrentIndex(0);
      setIsFlipped(false);
      setSessionDone(false);
      setReviewed(0);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không thể tải thẻ ôn tập");
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId, filterByScope]);

  // Load ALL saved vocab regardless of SRS schedule — for quiz modes
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: 1, limit: 1000 };
      if (scope === "folder" && scopeId) params.folderId = scopeId;
      const res = await getUserVocabs(params);
      let all = (res?.data ?? []).map(flattenCard);
      if (scope === "topic") all = filterByScope(all);
      setCards(all);
      setCurrentIndex(0);
      setIsFlipped(false);
      setSessionDone(false);
      setReviewed(0);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không thể tải từ vựng");
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId, filterByScope]);

  const restart = useCallback(() => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionDone(false);
    setReviewed(0);
  }, []);

  const shuffleCards = useCallback(() => {
    setCards((prev) => [...prev].sort(() => Math.random() - 0.5));
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionDone(false);
    setReviewed(0);
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const currentCard = cards[currentIndex] ?? null;
  const flipCard = useCallback(() => setIsFlipped((f) => !f), []);

  const advance = useCallback(() => {
    const next = currentIndex + 1;
    if (next >= cards.length) {
      setSessionDone(true);
    } else {
      setCurrentIndex(next);
      setIsFlipped(false);
    }
    setReviewed((r) => r + 1);
  }, [currentIndex, cards.length]);

  const handleAction = useCallback(
    async (action) => {
      if (!currentCard) return;
      try {
        await reviewUserVocab(currentCard._id, action);
      } catch {
        // non-blocking: SRS update failure shouldn't break the session
      }
      advance();
    },
    [currentCard, advance]
  );

  return {
    cards,
    currentCard,
    currentIndex,
    totalCards: cards.length,
    isFlipped,
    loading,
    sessionDone,
    reviewed,
    flipCard,
    handleAction,
    advanceCard: advance,
    restart,
    shuffleCards,
    fetchAll,
    refetchSRS: fetchCards,
  };
}
